/* eslint-disable @typescript-eslint/no-unused-vars */
import { AutocompleteWithLabel } from '@adotel/components'
import { useLazyQuery, useQuery } from '@apollo/client'
import { Typography } from '@material-ui/core'
import React, { FC, useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import TimePeriodForm from '../../components/TimePeriodForm'
import { ACCOMMODATIONS_QUERY } from '../../graphql/accommodation'
import { PROVIDERS_SUBMIT_LIST_QUERY } from '../../graphql/accommodationProviderSubmit'
import { RATEPLANS_QUERY } from '../../graphql/rateplan'
import { ROOMTYPES_QUERY } from '../../graphql/roomtype'
import { TENANTS_QUERY } from '../../graphql/tenant'
import { DateObject } from '../../helpers/date'
import { getRoleFromStorage } from '../../helpers/userData'
import { getEntityCode } from '../../helpers/userData'
import { getTranslate, lang } from '../../localization'
import { ROLE } from '../../staticRoleManagement/types'
import { Accommodation } from '../../types/accommodation'
import { ProviderSubmit } from '../../types/accommodationProviderSubmit'
import { ResetInventoryInput } from '../../types/inventory'
import { ProviderBrief } from '../../types/provider'
import { RatePlanBrief } from '../../types/rateplan'
import { RoomTypeBrief } from '../../types/roomtype'
import { Tenant } from '../../types/tenants'
import InventoryTypeSelection from './InventoryTypeSelection'
import useStyles from './styles/InventoryBulkUpdate.style'

interface InventoryBasicFiltersProps {
  hasAccessToChangeTenant: boolean
  hasAccessToChangeAccommodation: boolean
  isChannelSelected: boolean
  setisChannelSelected: React.Dispatch<React.SetStateAction<boolean>>
  input: ResetInventoryInput
  setinput: React.Dispatch<React.SetStateAction<ResetInventoryInput>>
}
const InventoryBasicFilters: FC<InventoryBasicFiltersProps> = ({
  hasAccessToChangeTenant,
  hasAccessToChangeAccommodation,
  isChannelSelected,
  setisChannelSelected,
  input,
  setinput
}) => {
  const classes = useStyles()
  const [autoCompleteLabelTenant, setautoCompleteLabelTenant] =
    useState<any>(null)
  const [autoCompleteLabelAccommodation, setautoCompleteLabelAccommodation] =
    useState<any>(null)
  const [autoCompleteLabelProvider, setautoCompleteLabelProvider] =
    useState<any>(null)
  const [autoCompleteLabelRoomType, setautoCompleteLabelRoomType] =
    useState<any>(null)
  const [autoCompleteLabelRatePlan, setautoCompleteLabelRatePlan] =
    useState<any>(null)

  const [dateObject, setdateObject] = useState<DateObject>()
  const [datePickerIsOpen, setdatePickerIsOpen] = useState<boolean>(false)
  const [timePickerSelected, setTimePickerSelected] = useState<boolean>(false)

  const [getTenants, { data: tenantsData, loading: loadingForTenantsList }] =
    useLazyQuery<{
      tenants: Tenant[]
    }>(TENANTS_QUERY)

  useEffect(() => {
    if (hasAccessToChangeTenant) {
      getTenants()
    }
  }, [])
  const validTenantsRoles = [
    ROLE.TENANT,
    ROLE.TENANT_MARKETING,
    ROLE.TENANT_IT,
    ROLE.TENANT_FINANCE,
    ROLE.TENANT_MANAGEMENT,
    ROLE.TENANT_PR
  ]
  const isRoleTenant = validTenantsRoles.some(
    (role) => role === getRoleFromStorage()
  )
  const [getAccommodations, { data: accommodationsData }] = useLazyQuery<{
    accommodations: Accommodation[]
  }>(ACCOMMODATIONS_QUERY, { notifyOnNetworkStatusChange: true })

  useEffect(() => {
    if (hasAccessToChangeAccommodation && autoCompleteLabelTenant) {
      getAccommodations({
        variables: {
          tenantId: autoCompleteLabelTenant.value
        }
      })
    } else if (hasAccessToChangeAccommodation && isRoleTenant) {
      getAccommodations({
        variables: {
          tenantCode: getEntityCode()
        }
      })
    }
  }, [autoCompleteLabelTenant])

  const [
    getProviderConnectionList,
    { data: providerListData, loading: loadingForProviderSubmitList }
  ] = useLazyQuery<{
    providersSubmitList: ProviderSubmit[]
  }>(PROVIDERS_SUBMIT_LIST_QUERY, {
    notifyOnNetworkStatusChange: true,
    onError: (error) => {
      toast.error(error.message)
    }
  })

  const [getRoomTypes, { data: roomtypes, loading: loadingForRoomtypes }] =
    useLazyQuery<{
      roomTypes: RoomTypeBrief[]
    }>(ROOMTYPES_QUERY)
  const [getRateplans, { data: rateplans, loading: loadingForRatePlans }] =
    useLazyQuery<{
      ratePlans: RatePlanBrief[]
    }>(RATEPLANS_QUERY)
  useEffect(() => {
    if (input.propertyCode) {
      getProviderConnectionList({
        variables: {
          propertyCode: autoCompleteLabelAccommodation.code
        }
      })
      getRoomTypes({
        variables: {
          propertyCode: autoCompleteLabelAccommodation.code,
          showDeletedRoomTypes: false,
          showDeactivedRoomTypes: false
        }
      })
    }
  }, [input.propertyCode])
  useEffect(() => {
    if (input.propertyCode && input.roomtypeCode) {
      getRateplans({
        variables: {
          propertyCode: autoCompleteLabelAccommodation.code,
          roomTypeCode: autoCompleteLabelRoomType.code,
          providerCode: autoCompleteLabelProvider.value,
          showDeletedRatePlans: false,
          showDeactivedRatePlans: false
        }
      })
    }
  }, [input.roomtypeCode])

  useEffect(() => {
    if (dateObject) {
      setinput({
        ...input,
        startDate: dateObject.gregorianStartDate || '',
        endDate: dateObject.gregorianEndDate || ''
      })
    }
  }, [dateObject])

  return (
    <div className={classes.basicFiltersContainer}>
      <div className={classes.form}>
        {hasAccessToChangeTenant && (
          <div className={classes.formItem}>
            <AutocompleteWithLabel
              value={autoCompleteLabelTenant}
              label={getTranslate('Tenant')}
              placeholder={getTranslate('Select accommodation tenant')}
              includeInputInList
              onChange={(_, v: any) => {
                if (v) {
                  setautoCompleteLabelTenant({
                    value: v.value,
                    label: v.label
                  })
                } else {
                  setautoCompleteLabelTenant(null)
                }
                setautoCompleteLabelAccommodation(null)
                setautoCompleteLabelProvider(null)
                setautoCompleteLabelRoomType(null)
                setautoCompleteLabelRatePlan(null)
                setinput({
                  ...input,
                  propertyCode: '',
                  providerCode: '',
                  roomtypeCode: '',
                  rateplanCode: ''
                })
              }}
              getOptionSelected={(option, value) =>
                option.value === value.value && option.label === value.label
              }
              getOptionLabel={(option) => option.label || ''}
              options={
                tenantsData?.tenants.map((item) => ({
                  label: item.basicDetails.name,
                  value: item.basicDetails?.tenantId || '',
                  code: item.basicDetails?.tenantCode || ''
                })) || []
              }
              renderOption={(option) => <>{option.label}</>}
              size="small"
              inputSize="small"
              loading={loadingForTenantsList}
              loadingText={getTranslate('Loading') + '...'}
            />
          </div>
        )}
        {hasAccessToChangeAccommodation && (
          <div className={classes.formItem}>
            <AutocompleteWithLabel
              label={getTranslate('Accommodation')}
              placeholder={getTranslate('Select accommodation')}
              includeInputInList
              onChange={(event, v: any) => {
                if (v) {
                  setautoCompleteLabelAccommodation({
                    value: v.value,
                    label: v.label,
                    code: v.code
                  })
                  setinput({
                    ...input,
                    propertyCode: v.code,
                    providerCode: '',
                    roomtypeCode: undefined,
                    rateplanCode: undefined
                  })
                } else {
                  setautoCompleteLabelAccommodation(null)
                  setinput({
                    ...input,
                    propertyCode: '',
                    providerCode: '',
                    roomtypeCode: undefined,
                    rateplanCode: undefined
                  })
                }
                setautoCompleteLabelProvider(null)
                setautoCompleteLabelRoomType(null)
                setautoCompleteLabelRatePlan(null)
              }}
              getOptionSelected={(option, value) =>
                option.value === value.value && option.label === value.label
              }
              getOptionLabel={(option) => option.label || ''}
              options={
                accommodationsData
                  ? accommodationsData?.accommodations.map((item) => ({
                      label: item.basicDetails.name,
                      value: item.basicDetails.accommodationID || '',
                      code: item.basicDetails.propertyCode
                    }))
                  : []
              }
              value={autoCompleteLabelAccommodation}
              renderOption={(option) => <>{option.label}</>}
              size="small"
              inputSize="small"
              loading={loadingForTenantsList}
              loadingText={getTranslate('Please wait...')}
            />
          </div>
        )}
        <div className={classes.formItem}>
          <AutocompleteWithLabel
            label={getTranslate('Provider')}
            placeholder={getTranslate('Select Provider')}
            includeInputInList
            onChange={(event, v: any) => {
              if (v) {
                setautoCompleteLabelProvider({
                  value: v.value,
                  label: v.label,
                  code: v.code
                })
                setinput({
                  ...input,
                  providerCode: v.value,
                  roomtypeCode: undefined,
                  rateplanCode: undefined
                })
              } else {
                setautoCompleteLabelProvider(null)
                setinput({
                  ...input,
                  providerCode: '',
                  roomtypeCode: undefined,
                  rateplanCode: undefined
                })
              }
              setautoCompleteLabelRoomType(null)
              setautoCompleteLabelRatePlan(null)
            }}
            getOptionSelected={(option, value) =>
              option.value === value.value && option.label === value.label
            }
            getOptionLabel={(option) => option.label || ''}
            value={autoCompleteLabelProvider}
            options={
              providerListData?.providersSubmitList?.map((p) => ({
                label:
                  lang === 'fa' &&
                  p.providerNameTranslatedList &&
                  p.providerNameTranslatedList[0].value
                    ? p.providerNameTranslatedList[0].value
                    : p.providerName || '',
                value: p.providerCode || ''
              })) || []
            }
            renderOption={(option) => <>{option.label}</>}
            size="small"
            inputSize="small"
            loading={loadingForProviderSubmitList}
            loadingText={getTranslate('Loading') + '...'}
          />
        </div>
        <div className={classes.formItem}>
          <AutocompleteWithLabel
            label={getTranslate('Room Type')}
            placeholder={getTranslate('Select Room type')}
            includeInputInList
            onChange={(event, v: any) => {
              if (v) {
                setautoCompleteLabelRoomType({
                  value: v.value,
                  label: v.label,
                  code: v.code
                })
                setinput({
                  ...input,
                  roomtypeCode: v.code,
                  rateplanCode: undefined
                })
              } else {
                setautoCompleteLabelRoomType(null)
                setinput({
                  ...input,
                  roomtypeCode: undefined,
                  rateplanCode: undefined
                })
              }
              setautoCompleteLabelRatePlan(null)
            }}
            getOptionSelected={(option, value) =>
              option.value === value.value && option.label === value.label
            }
            getOptionLabel={(option) => option.label || ''}
            value={autoCompleteLabelRoomType}
            options={
              roomtypes?.roomTypes?.map((roomtype) => ({
                label: `${roomtype.name} (${roomtype.displayRoomTypeCode})`,
                value: roomtype.id || '',
                code: roomtype.displayRoomTypeCode
              })) || []
            }
            renderOption={(option) => <>{option.label}</>}
            size="small"
            inputSize="small"
            loading={loadingForRoomtypes}
            loadingText={getTranslate('Loading') + '...'}
          />
        </div>
        <div className={classes.formItem}>
          <AutocompleteWithLabel
            label={getTranslate('Rateplan')}
            placeholder={getTranslate('Select Rateplan')}
            includeInputInList
            onChange={(event, v: any) => {
              if (v) {
                setautoCompleteLabelRatePlan({
                  value: v.value,
                  label: v.label,
                  code: v.code
                })
                setinput({
                  ...input,
                  rateplanCode: v.code
                })
              } else {
                setautoCompleteLabelRatePlan(null)
                setinput({
                  ...input,
                  rateplanCode: undefined
                })
              }
            }}
            value={autoCompleteLabelRatePlan}
            getOptionSelected={(option, value) =>
              option.value === value.value && option.label === value.label
            }
            getOptionLabel={(option) => option.label || ''}
            options={
              rateplans?.ratePlans.map((rateplan) => ({
                label: `${rateplan.name} (${rateplan.displayRatePlanCode})`,
                value: rateplan.id || '',
                code: rateplan.displayRatePlanCode
              })) || []
            }
            renderOption={(option) => <>{option.label}</>}
            size="small"
            inputSize="small"
            loading={loadingForRatePlans}
            loadingText={getTranslate('Loading') + '...'}
          />
        </div>
        <InventoryTypeSelection
          {...{
            input,
            setinput,
            isChannelSelected,
            setisChannelSelected
          }}
        />
      </div>
      <div className={classes.dateSelectorForm}>
        <Typography>{getTranslate('Time Period')}</Typography>
        <TimePeriodForm
          // just to skip warning. there'cs no good choise for key in here
          isOpen={datePickerIsOpen && timePickerSelected}
          endDateCanBeSelectAsStartDate
          toggleDatePicker={() => {
            setdatePickerIsOpen(!datePickerIsOpen)
          }}
          dateObject={dateObject}
          onChange={setdateObject}
          setTimePickerSelected={setTimePickerSelected}
        />
      </div>
    </div>
  )
}

export default InventoryBasicFilters
